import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Header from '../Layout/Header';
import Sidebar from '../Layout/Sidebar';
import RouteSelector from '../Dashboard/RouteSelector';

const airportTimezones = {
  'HND': 'Asia/Tokyo', // Tokyo Haneda, Japan (UTC+09:00)
  'NRT': 'Asia/Tokyo', // Tokyo Narita, Japan (UTC+09:00)
  'KIX': 'Asia/Tokyo', // Osaka, Japan (UTC+09:00)
  'NGO': 'Asia/Tokyo', // Nagoya, Japan (UTC+09:00)
  'HNL': 'Pacific/Honolulu', // Honolulu, Hawaii (UTC-10:00)
  'JFK': 'America/New_York', // New York JFK (UTC-05:00/-04:00 with DST)
  'BOS': 'America/New_York', // Boston (UTC-05:00/-04:00 with DST)
  'ORD': 'America/Chicago', // Chicago (UTC-06:00/-05:00 with DST)
  'DFW': 'America/Dallas', // Dallas (UTC-06:00/-05:00 with DST)
  'LAX': 'America/Los_Angeles', // Los Angeles (UTC-08:00/-07:00 with DST)
  'SAN': 'America/Los_Angeles', // San Diego (UTC-08:00/-07:00 with DST)
  'SFO': 'America/Los_Angeles', // San Francisco (UTC-08:00/-07:00 with DST)
  'SEA': 'America/Los_Angeles', // Seattle (UTC-08:00/-07:00 with DST)
  'YVR': 'America/Los_Angeles', // Vancouver (UTC-08:00/-07:00 with DST)
};

const TrendsPage = () => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [timeRange, setTimeRange] = useState('3months');
  const [groupBy, setGroupBy] = useState('depart_date');
  const [trendDirection, setTrendDirection] = useState('past'); // 'past' or 'future'
  
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }
    
    fetch('/api/auth/verify', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(res => res.json())
    .then(data => {
      if (!data.authenticated) {
        localStorage.removeItem('authToken');
        navigate('/login');
      }
    })
    .catch(err => {
      console.error('Auth verification error:', err);
      navigate('/login');
    });

    const fetchRoutes = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch('/api/flights/routes', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) throw new Error('Failed to fetch routes');
        
        const data = await response.json();
        setRoutes(data);
        
        if (data.length > 0) {
          setSelectedRoute({
            origin: data[0].origin,
            destination: data[0].destination
          });
        }
      } catch (err) {
        setError('Failed to load routes. Please try again.');
        console.error('Route fetch error:', err);
      }
    };

    fetchRoutes();
  }, [navigate]);
  
  useEffect(() => {
    if (!selectedRoute) return;

    const getDateRange = () => {
      const today = new Date(); // Today: 2025-04-25
      let startDate, endDate;
      
      if (trendDirection === 'past') {
        endDate = new Date(today);
        startDate = new Date(today);
        
        switch (timeRange) {
          case '1month':
            startDate.setMonth(startDate.getMonth() - 1);
            break;
          case '3months':
            startDate.setMonth(startDate.getMonth() - 3);
            break;
          case '6months':
            startDate.setMonth(startDate.getMonth() - 6);
            break;
          case '1year':
            startDate.setFullYear(startDate.getFullYear() - 1);
            break;
          default:
            startDate.setMonth(startDate.getMonth() - 3);
        }
      } else {
        startDate = new Date(today);
        endDate = new Date(today);
        
        switch (timeRange) {
          case '1month':
            endDate.setMonth(endDate.getMonth() + 1);
            break;
          case '3months':
            endDate.setMonth(endDate.getMonth() + 3);
            break;
          case '6months':
            endDate.setMonth(endDate.getMonth() + 6);
            break;
          case '1year':
            endDate.setFullYear(endDate.getFullYear() + 1);
            break;
          default:
            endDate.setMonth(endDate.getMonth() + 3);
        }
      }
      
      return {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      };
    };

    const fetchStats = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const { origin, destination } = selectedRoute;
        const dateRange = getDateRange();
        
        const url = new URL('/api/flights/stats', window.location.origin);
        url.searchParams.append('origin', origin);
        url.searchParams.append('destination', destination);
        url.searchParams.append('startDate', dateRange.startDate);
        url.searchParams.append('endDate', dateRange.endDate);
        url.searchParams.append('groupBy', groupBy);
        
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) throw new Error('Failed to fetch stats');
        
        const data = await response.json();
        setStats(data);
      } catch (err) {
        setError('Failed to load statistics. Please try again.');
        console.error('Stats fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [selectedRoute, timeRange, groupBy, trendDirection]);
  
  const handleRouteChange = (route) => {
    setSelectedRoute(route);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr || !selectedRoute) return '';
    const timezone = airportTimezones[selectedRoute.origin] || 'UTC';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      timeZone: timezone
    });
  };
  
  const formatChartData = () => {
    if (!stats || stats.length === 0) return [];
    
    return stats.map(item => ({
      date: groupBy === 'depart_date' ? formatDate(item[groupBy]) : item[groupBy],
      min: Math.round(item.min_points),
      avg: Math.round(item.avg_points),
      max: Math.round(item.max_points),
      count: item.flight_count
    }));
  };
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md text-xs">
          <p className="font-semibold mb-1">{label}</p>
          <p className="text-green-600">
            Lowest: {payload[0].value.toLocaleString()} points
          </p>
          <p className="text-blue-600">
            Average: {payload[1].value.toLocaleString()} points
          </p>
          <p className="text-orange-600">
            Highest: {payload[2].value.toLocaleString()} points
          </p>
        </div>
      );
    }
    return null;
  };
  
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };
  
  const chartData = formatChartData();
  
  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onLogout={handleLogout} />
        <main className="flex-1 overflow-y-auto p-4">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-800 mb-4">
                Award Points Trends
              </h1>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <RouteSelector 
                    routes={routes} 
                    selectedRoute={selectedRoute} 
                    onChange={handleRouteChange} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time Range
                  </label>
                  <select
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="1month">{trendDirection === 'past' ? 'Last Month' : 'Next Month'}</option>
                    <option value="3months">{trendDirection === 'past' ? 'Last 3 Months' : 'Next 3 Months'}</option>
                    <option value="6months">{trendDirection === 'past' ? 'Last 6 Months' : 'Next 6 Months'}</option>
                    <option value="1year">{trendDirection === 'past' ? 'Last Year' : 'Next Year'}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trend Direction
                  </label>
                  <select
                    value={trendDirection}
                    onChange={(e) => setTrendDirection(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="past">Past Trends</option>
                    <option value="future">Future Trends</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group By
                  </label>
                  <select
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="depart_date">Departure Date</option>
                    <option value="cabin_class">Cabin Class</option>
                    <option value="bundle_code">Bundle Code</option>
                  </select>
                </div>
              </div>
              {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                  {error}
                </div>
              )}
            </div>
            {loading ? (
              <div className="bg-white rounded-lg shadow p-6 flex justify-center items-center h-64">
                <div className="text-gray-500 flex items-center">
                  <svg className="animate-spin h-5 w-5 mr-3 text-blue-500" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading trend data...
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Points Trend Over Time
                  </h2>
                  {chartData.length > 0 ? (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={chartData}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }} 
                            interval="preserveStartEnd" 
                          />
                          <YAxis 
                            tick={{ fontSize: 12 }} 
                            width={80}
                            tickFormatter={(value) => value.toLocaleString()}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="min" 
                            stroke="#10b981" 
                            name="Lowest Points" 
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="avg" 
                            stroke="#2563eb" 
                            name="Average Points" 
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="max" 
                            stroke="#f97316" 
                            name="Highest Points" 
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="flex justify-center items-center h-64 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">No data available for the selected criteria.</p>
                    </div>
                  )}
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Flight Availability
                  </h2>
                  {chartData.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={chartData}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }} 
                            interval="preserveStartEnd" 
                          />
                          <YAxis 
                            tick={{ fontSize: 12 }} 
                            width={50}
                          />
                          <Tooltip />
                          <Legend />
                          <Bar 
                            dataKey="count" 
                            fill="#3b82f6" 
                            name="Number of Flights" 
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="flex justify-center items-center h-64 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">No data available for the selected criteria.</p>
                    </div>
                  )}
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Statistics Summary
                  </h2>
                  {stats.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {groupBy === 'depart_date' ? 'Date' : groupBy === 'cabin_class' ? 'Cabin Class' : 'Bundle Code'}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Lowest Points
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Average Points
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Highest Points
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Flights
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {stats.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {groupBy === 'depart_date' ? formatDate(item[groupBy]) : item[groupBy] || 'Unknown'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                                {item.min_points.toLocaleString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                                {Math.round(item.avg_points).toLocaleString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                                {item.max_points.toLocaleString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {item.flight_count}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="flex justify-center items-center h-32 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">No data available for the selected criteria.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default TrendsPage;